import { faker } from "@faker-js/faker";
import type { Addresses } from "../../modules/profiles/types/address.type";
import type {
  CreateProfileResponse,
  DirectorsType,
  ProfileExportType,
  ShareholdersType,
  UBOsType,
} from "../../modules/profiles/types/createProfile.type";
import {
  type Audits,
  type KeyPartiesType,
  KycProfileStatus,
  type MockGetAudits,
  ModeOfCreation,
  type Profile,
  ProfileTypes,
  RiskRatings as RiskRatingsEnum,
  type UboDetails,
  type ViewNotes,
} from "../../modules/profiles/types/profile.type";
import type {
  ProfileDetails,
  ProfileDetailsResponse,
} from "../../modules/profiles/types/profileDetails.type";
import {
  KeyPartyType,
  ProfileStatus,
  ProfileTabsTypes,
  RiskRating,
} from "../../shared/constants/common.constant";
import type { Tab as TabType } from "../../shared/types/tab.type";
import { Gender } from "../../modules/aml/aml-utils/amlCheckUtils";

export const profileBuilder = (overrides = {}): Profile => {
  return {
    id: faker.datatype.uuid(),
    name: faker.company.name(),
    riskRating: faker.helpers.arrayElement([
      RiskRating.VeryHigh,
      RiskRating.High,
      RiskRating.Medium,
      RiskRating.Low,
      RiskRating.VeryLow,
      RiskRating.NotApplicable,
    ]),
    status: faker.helpers.arrayElement([
      ProfileStatus.new,
      ProfileStatus.approved,
      ProfileStatus.declined,
      ProfileStatus.pending,
      ProfileStatus.cancelled,
      ProfileStatus.referred,
      ProfileStatus.closed,
      ProfileStatus.approvedReviewDue,
    ]),
    assignedTo: faker.helpers.arrayElement([faker.internet.userName(), ""]),
    modifiedAt: faker.date.past().toISOString(),
    type: faker.helpers.arrayElement([
      "trust",
      "individual",
      "soleTrader",
      "company",
      "pLC",
      "partnership",
      "otherEntity",
    ]),
    internalId: faker.random.alphaNumeric(6),
    customerId: faker.random.alphaNumeric(6),
    assignedToId: faker.datatype.number(6),
    safeNumber: faker.random.alphaNumeric(6),
    organisationNumber: faker.random.alphaNumeric(6),
    createdAt: faker.date.past().toISOString(),
    createdById: faker.date.past().toISOString(),
    createdBy: faker.internet.userName(),
    modifiedById: faker.random.alphaNumeric(6),
    modifiedByUsername: faker.internet.userName(),
    kycApprovedAt: faker.date.past().toISOString(),
    kycStatusUpdatedOn: faker.date.past().toISOString(),
    kycReviewOn: faker.date.past().toISOString(),
    kycComments: faker.lorem.paragraph(),
    details: {
      legalName: faker.internet.domainWord(),
    },
    keyPartyCount: 0,
    noteCount: faker.datatype.number(2),
    attachmentCount: faker.datatype.number(2),
    isLocked: false,
    modeOfCreation: "manual",
    companyId: "",
    ...overrides,
  };
};

export const profileTabsBuilder = (): TabType[] => [
  {
    id: ProfileTabsTypes.business,
    label: "profile.tabs.business",
    noOfItems: 0,
    name: "business",
  },
  {
    id: ProfileTabsTypes.individual,
    label: "profile.tabs.individual",
    noOfItems: 0,
    name: "individual",
  },
  {
    id: ProfileTabsTypes.keyParties,
    label: "profile.tabs.keyParties",
    noOfItems: 0,
    name: "key-parties",
  },
  {
    id: ProfileTabsTypes.ubos,
    label: "profile.tabs.ubos",
    noOfItems: 0,
    name: "ubos",
  },
  {
    id: ProfileTabsTypes.groupStructure,
    label: "profile.tabs.groupStructure",
    noOfItems: 0,
    name: "group-structure",
  },
  {
    id: ProfileTabsTypes.linkedProfiles,
    label: "profile.tabs.linkedProfiles",
    noOfItems: 0,
    name: "linked-profiles",
  },
  {
    id: ProfileTabsTypes.openAlerts,
    label: "profile.tabs.openAlerts",
    noOfItems: 0,
    name: "open-alerts",
  },
  {
    id: ProfileTabsTypes.audits,
    label: "profile.tabs.audits",
    noOfItems: 0,
    name: "audits",
  },
];

export const profileDetailsBuilder = (): ProfileDetails => {
  return {
    legalName: faker.company.name(),
    tradingName: faker.company.name(),
    aliases: [faker.company.name()],
    activity: faker.random.word(),
    vatNo: faker.random.alphaNumeric(10),
    dateOfBirth: faker.date.past().toISOString(),
    gender: faker.helpers.arrayElement(Object.values(Gender)),
    turnover: {
      currencyCode: "usd",
      amount: Number(faker.random.numeric(10)),
    },
    assetsUnderManagement: {
      currencyCode: "usd",
      amount: Number(faker.random.numeric(10)),
    },
    description: faker.random.words(),
    organizationNumber: faker.random.numeric(7),
    isListedOnExchange: true,
    exchangeName: faker.random.word(),
    contactName: faker.company.name(),
    website: faker.random.words(),
    email: faker.internet.email(),
    telephone: faker.random.numeric(10),
    internalContact: faker.company.name(),
    internalEmail: faker.internet.email(),
    createdAt: faker.date.recent().toISOString(),
    createdBy: faker.name.fullName(),
    createdById: 1,
    noteCount: 2,
    attachmentCount: 3,
    modeOfCreation: faker.helpers.arrayElement([
      ModeOfCreation.manual,
      ModeOfCreation.import,
      ModeOfCreation.batchUpload,
    ]),
    safeNumber: faker.random.word(),
    formationDate: faker.date.recent().toISOString(),
    internationalScore: faker.random.word(),
  };
};

export const profileDetailsResponseBuilder = (
  override = {}
): ProfileDetailsResponse => {
  return {
    profileId: faker.datatype.uuid(),
    legalName: faker.company.name(),
    tradingName: faker.company.name(),
    aliases: [faker.company.name(), faker.company.name()],
    activity: faker.random.word(),
    description: faker.random.words(),
    contactName: faker.name.fullName(),
    email: faker.internet.email(),
    website: faker.internet.url(),
    telephone: faker.phone.number(),
    turnover: {
      currencyCode: "usd",
      amount: faker.datatype.number(),
    },
    assetsUnderManagement: {
      currencyCode: "usd",
      amount: faker.datatype.number(),
    },
    dateOfBirth: faker.date.birthdate(),
    gender: faker.helpers.arrayElement(Object.values(Gender)),
    countryCode: "usd",
    vatNo: faker.random.numeric(),
    isListedOnExchange: faker.datatype.boolean(),
    exchangeName: faker.company.name(),
    organizationNumber: faker.random.numeric(),
    internalContact: faker.name.fullName(),
    internalEmail: faker.internet.email(),
    createdAt: faker.date.past().toISOString(),
    createdById: faker.datatype.number(),
    createdBy: faker.name.fullName(),
    modifiedAt: faker.date.recent().toISOString(),
    modifiedById: faker.datatype.number(),
    modifiedBy: faker.datatype.number(),
    noteCount: faker.datatype.number(),
    attachmentCount: faker.datatype.number(),
    ...override,
  };
};

export const profileResponseBuilder = (
  overrides = {}
): CreateProfileResponse => {
  return {
    id: faker.datatype.uuid(),
    name: faker.company.name(),
    riskRating: faker.helpers.arrayElement([
      RiskRatingsEnum.veryHigh,
      RiskRatingsEnum.high,
      RiskRatingsEnum.medium,
      RiskRatingsEnum.low,
      RiskRatingsEnum.veryLow,
      RiskRatingsEnum.notApplicable,
    ]),
    status: faker.helpers.arrayElement([
      KycProfileStatus.new,
      KycProfileStatus.approved,
      KycProfileStatus.declined,
      KycProfileStatus.pending,
      KycProfileStatus.cancelled,
      KycProfileStatus.referred,
      KycProfileStatus.closed,
      KycProfileStatus.approvedReviewDue,
    ]),
    type: faker.helpers.arrayElement([
      ProfileTypes.trust,
      ProfileTypes.individual,
      ProfileTypes.soleTrader,
      ProfileTypes.company,
      ProfileTypes.plc,
      ProfileTypes.partnership,
      ProfileTypes.otherEntity,
    ]),
    internalId: faker.random.alphaNumeric(6),
    assignedToId: faker.datatype.number(6),
    assignedTo: faker.helpers.arrayElement([faker.internet.userName(), ""]),
    safeNumber: faker.random.alphaNumeric(6),
    formationDate: faker.date.past().toISOString(),
    createdAt: faker.date.past().toISOString(),
    createdById: faker.datatype.number(),
    createdBy: faker.internet.userName(),
    modifiedAt: faker.date.past().toISOString(),
    modifiedById: faker.datatype.number(),
    modifiedBy: faker.internet.userName(),
    kycApprovedAt: faker.date.past().toISOString(),
    kycReviewOn: faker.date.past().toISOString(),
    kycStatusUpdatedOn: faker.date.past().toISOString(),
    kycComments: faker.lorem.paragraph(),
    noteCount: faker.datatype.number(2),
    attachmentCount: faker.datatype.number(),
    keyPartyCount: faker.datatype.number(),
    uboCount: faker.datatype.number(),
    openAlertCount: faker.datatype.number(),
    details: {
      ...profileDetailsResponseBuilder(),
    },
    ...overrides,
  };
};

export const addressesBuilder = (): Addresses => {
  return {
    id: faker.random.alphaNumeric(10),
    buildingDetails: faker.random.word(),
    street: faker.random.word(),
    city: faker.random.word(),
    region: faker.random.word(),
    postalCode: faker.random.numeric(5),
    countryCode: faker.random.word(),
    type: faker.helpers.arrayElement(["registered", "trading"]),
    description: faker.random.alpha(10),
    profileId: faker.datatype.uuid(),
  };
};

export const profileDetailSubTabs = (): TabType[] => [
  {
    id: ProfileTabsTypes.details,
    label: "profile.subTabs.details",
    noOfItems: 0,
    name: "details",
  },
  {
    id: ProfileTabsTypes.amlScreen,
    label: "profile.subTabs.amlScreen",
    noOfItems: 0,
    name: "amlScreen",
  },
];

export const profileAlertSubTabs = (): TabType[] => [
  {
    id: ProfileTabsTypes.amlAlerts,
    label: "profile.subTabs.amlAlerts",
    noOfItems: 0,
    name: "amlAlerts",
  },
  {
    id: ProfileTabsTypes.kycAlerts,
    label: "profile.subTabs.kycAlerts",
    noOfItems: 0,
    name: "kycAlerts",
  },
];

export const directorsBuilder = (): DirectorsType => {
  return {
    id: faker.datatype.uuid(),
    name: faker.random.word(),
    position: faker.random.word(),
    country: faker.helpers.arrayElement(["af", "ax", "al", "ad", ""]),
    dateOfBirth: faker.date.past().toISOString(),
    gender: faker.helpers.arrayElement(["Male", "Female", ""]),
    isSelected: false,
    role: faker.helpers.arrayElement(["CTO", "CEO"]),
  };
};

export const shareholdersBuilder = (): ShareholdersType => {
  return {
    id: faker.datatype.uuid(),
    name: faker.random.word(),
    country: faker.helpers.arrayElement(["af", "ax", "al", "ad", ""]),
    sharesPercentage: faker.helpers.arrayElement([22, 45, 10, 80, 90]),
    type: faker.helpers.arrayElement(["Company", "Individual"]),
    entityType: faker.helpers.arrayElement([
      ProfileTypes.company,
      ProfileTypes.individual,
    ]),
    isSelected: false,
  };
};

export const ubosBuilder = (): UBOsType => {
  return {
    id: faker.datatype.uuid(),
    name: faker.random.word(),
    country: faker.helpers.arrayElement(["af", "ax", "al", "ad", ""]),
    type: faker.helpers.arrayElement(["Company", "Individual", ""]),
    isSelected: false,
  };
};

export const generateAuditTableDetails = (): Audits => {
  const datasetCount = faker.datatype.number({ min: 0, max: 3 });
  return {
    payload: { profileName: faker.random.word() },
    category: Array.from({ length: 1 }, () =>
      faker.helpers.arrayElement(["amlSearch", "profile", "uploads"])
    ),
    subcategory:
      Math.random() < 0.5
        ? []
        : Array.from({ length: 1 }, () =>
            faker.helpers.arrayElement([
              "allSubCategories",
              "amlMonitoring",
              "keyParty",
              "kycMonitoring",
              "profile",
              "profileDetails",
              "searchHits",
            ])
          ),
    action: Array.from({ length: 1 }, () =>
      faker.helpers.arrayElement([
        "addedToAmlMonitoring",
        "addedToKycMonitoring",
        "addressCreated",
        "addressDeleted",
        "addressUpdated",
        "alertUpdated",
        "attachmentCreated",
        "attachmentDeleted",
        "attachmentUpdated",
        "created",
        "deleted",
        "kycStatusUpdated",
        "noteArchived",
        "noteCreated",
        "noteDeleted",
        "noteUnarchived",
        "noteUpdated",
        "removedFromAmlMonitoring",
        "removedFromKycMonitoring",
        "searchDeleted",
        "searchLinked",
        "searchRemoved",
        "updated",
        "updatedInAmlMonitoring",
        "recharged",
      ])
    ),
    description: Array.from({ length: 1 }, () => faker.random.word()),
    users: Array.from({ length: 1 }, () => faker.random.word()),
    createdAt: faker.date.past().toISOString(),
    createdById: faker.random.word(),
    createdBy: Array.from({ length: 1 }, () => faker.random.word()),
    datasets: Array.from({ length: datasetCount }, () =>
      faker.helpers.arrayElement([
        "Enfys",
        "Lowe",
        "Profile Created",
        "Profile Asd created",
        "19-08-2023 09:24:07",
        "12345678",
      ])
    ),
  };
};

export const auditsBuilder = (count: number): MockGetAudits => {
  return {
    count,
    apiFailed: faker.datatype.boolean(),
    items: Array.from({ length: count }, () => generateAuditTableDetails()),
  };
};

export const ProfileExportBuilder = (): ProfileExportType => {
  return {
    legalName: faker.random.word(),
    businessName: faker.random.word(),
    tradingName: faker.random.word(),
    aliases: Array.from({ length: 3 }, () => faker.random.word()),
    directors: Array.from({ length: 25 }, () => directorsBuilder()),
    shareHolders: Array.from({ length: 25 }, () => shareholdersBuilder()),
    ubos: Array.from({ length: 25 }, () => ubosBuilder()),
    directorsExport: [],
    shareHoldersExport: [],
    ubosExport: [],
    safeNumber: "",
    companyId: "",
    formationDate: "",
    addresses: [],
    organisationNumber: "",
    activity: "",
    description: "",
    website: "",
    telephone: "",
    email: "",
    vatNo: "",
    internationalScore: "",
    type: "",
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const MonitoringDetailsByIdDataBuilder = () => {
  return {
    scheduleId: faker.datatype.uuid(),
    emailRecipents: Array.from({ length: 3 }, () => faker.internet.email()),
    criteria: {
      name: faker.random.word(),
      firstName: faker.random.word(),
      middleName: faker.random.word(),
      lastName: faker.random.word(),
      gender: faker.helpers.arrayElement(["Male", "Female"]),
      dateOfBirth: faker.date.recent().toLocaleDateString(),
      countrycodes: Array.from({ length: 5 }, () =>
        faker.helpers.arrayElement(["af", "ax", "al", "ad"])
      ),
      threshold: faker.helpers.arrayElement([90, 95, 100]),
      datasets: Array.from({ length: 5 }, () =>
        faker.helpers.arrayElement([
          "sanction",
          "enforcement",
          "insolvencyRegister",
          "stateOwnedEnterprise",
          "pep",
          "profileOfInterest",
          "adverseMedia",
        ])
      ),
      pepTiers: ["PepTier 1"],
    },
    createdAt: faker.date.past().toISOString(),
    createdBy: faker.name.fullName(),
    modifiedAt: faker.date.recent().toISOString(),
    modifiedBy: faker.name.fullName(),
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const AmlSearchesIdDataBuilder = (type: string) => {
  return {
    id: faker.datatype.uuid(),
    name: "john",
    countryCodes: ["in"],
    threshold: 90,
    type: type,
    datasets: ["AM"],
    status: "new",
    riskRating: "notApplicable",
    assignedToUserId: 0,
    assignedUser: "1123556",
    createdById: 0,
    modifiedById: 0,
    note: "test check",
    totalHitCount: 0,
    truePositiveHitsCount: 0,
    falsePositiveHitsCount: 0,
    undecidedHitsCount: 1,
    firstName: "raj",
    middleName: "kumar",
    lastName: "edla",
    gender: "male",
    dateOfBirth: "2023-10-10",
    pepTiers: ["pepTier1"],
    createdAt: faker.date.past().toISOString(),
    createdBy: faker.name.fullName(),
    modifiedAt: faker.date.recent().toISOString(),
    modifiedBy: faker.name.fullName(),
    scheduleId: faker.helpers.arrayElement([faker.datatype.uuid(), null]),
  };
};

export const keyPartiesData: KeyPartiesType[] = [
  {
    id: faker.datatype.uuid(),
    firstName: "Gerald",
    middleName: "Patrick",
    lastName: "Farano",
    keyPartyType: "director",
    entityType: ProfileTypes.individual,
    gender: "male",
    dateOfBirth: "1966-09-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails: "2 Portman Street, London, W1H 6DU",
      street: "2 Portman Street",
      city: "London",
      postalCode: "W1H 6DU",
    },
    country: "US",
    role: "CHAIR PERSON",
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "Mani",
    middleName: "Manikanta",
    lastName: "Dangeti",
    keyPartyType: KeyPartyType.other,
    description: "This is Created for testing other",
    entityType: ProfileTypes.individual,
    gender: "male",
    dateOfBirth: "1966-09-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails: "testing street, Dubai, K1H 6DU",
      street: "testing street",
      city: "Dubai",
      postalCode: "K1H 6DU",
    },
    country: "US",
    createdAt: "2025-04-02T07:15:05.192931Z",
    createdById: 101557117,
    createdBy: "Supriya",
    modifiedAt: "2024-04-09T14:34:52.404785Z",
    modifiedById: 101563672,
    modifiedBy: "Manikanta Dangeti",
  },
  {
    id: faker.datatype.uuid(),
    name: "CSIN Pvt Ltd",
    keyPartyType: KeyPartyType.other,
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "0778143",
    address: {
      description: "Service Address",
      buildingDetails: "71 S. Wacker Drive Suite 1700, Chicago, Il 60606",
      street: "71 S. Wacker Drive Suite 1700",
      city: "Chicago",
      region: "Il 60606",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Khanishram",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Khanishram",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "Alexander",
    lastName: "Kuznetsov",
    keyPartyType: KeyPartyType.director,
    entityType: ProfileTypes.individual,
    gender: "female",
    dateOfBirth: "1978-09-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails: "4b Belovezhskaya Str, Moscow, 121353",
      street: "4b Belovezhskaya Str",
      city: "Moscow",
      postalCode: "121353",
    },
    country: "RU",
    role: "DIRECTOR",
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "Aleksey",
    lastName: "Ivanov",
    keyPartyType: KeyPartyType.director,
    entityType: ProfileTypes.individual,
    gender: "male",
    dateOfBirth: "1975-08-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails: "4b Belovezhskaya Str, Moscow, 121353",
      street: "4, Block B Belovezhskaya Street",
      city: "Moscow",
      postalCode: "121353",
    },
    country: "RU",
    role: "DIRECTOR",
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "Stanislav",
    lastName: "Kostinov",
    keyPartyType: KeyPartyType.director,
    entityType: ProfileTypes.individual,
    gender: "male",
    dateOfBirth: null,
    address: {
      description: "Service Address",
      buildingDetails: "4, Block B Belovezhskaya Street, Moscow, 121353",
      street: "4, Block B Belovezhskaya Street",
      city: "Moscow",
      postalCode: "121353",
    },
    country: null,
    role: null,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "Olesya",
    lastName: "Afanasyeva",
    keyPartyType: KeyPartyType.director,
    entityType: ProfileTypes.individual,
    gender: "male",
    dateOfBirth: "1980-04-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails:
        "215 9th Avenue Sw, Suite 750, Calgary, Alberta Canada T2p 1k3",
      street: "215 9th Avenue Sw, Suite 750",
      city: "Calgary",
      region: "Alberta Canada T2p 1k3",
    },
    country: "CA",
    role: "CHIEF FINANCIAL OFFICER",
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "James",
    middleName: "F.",
    lastName: "Herald",
    keyPartyType: KeyPartyType.director,
    entityType: ProfileTypes.individual,
    gender: "female",
    dateOfBirth: "1958-06-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails: "71 S. Wacker Drive Suite 1700, Chicago, Il 60606",
      street: "71 S. Wacker Drive Suite 1700",
      city: "Chicago",
      region: "Il 60606",
    },
    country: "US",
    role: "CEO",
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "EVRAZ GROUP S.A.",
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "COMPUTERSHARE DR NOMINEES LIMITED",
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 3.14,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "Evraz Plc",
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: faker.name.fullName(),
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784342",
    address: {
      buildingDetails:
        "5th Floor,, 6 St. Andrew Street, London, EC4A 3AE United Kingdom",
      street: "6 St. Andrew Street",
      city: "London",
      postalCode: "EC4A 3AE",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "Evraz Plc",
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784343",
    address: {
      description: "Service Address",
      buildingDetails: "71 S. Wacker Drive Suite 1700, Chicago, Il 60606",
      street: "71 S. Wacker Drive Suite 1700",
      city: "Chicago",
      region: "Il 60606",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "Evraz Plc",
    keyPartyType: "ubo",
    entityType: ProfileTypes.company,
    country: "GB",
    organisationNumber: "07784343",
    address: {
      description: "Service Address",
      buildingDetails: "71 S. Wacker Drive Suite 1700, Chicago, Il 60606",
      street: "71 S. Wacker Drive Suite 1700",
      city: "Chicago",
      region: "Il 60606",
    },
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    firstName: "James",
    middleName: "F.",
    lastName: "Herald",
    keyPartyType: "director",
    entityType: ProfileTypes.individual,
    gender: "female",
    dateOfBirth: "1958-06-01T00:00:00Z",
    address: {
      description: "Service Address",
      buildingDetails: "71 S. Wacker Drive Suite 1700, Chicago, Il 60606",
      street: "71 S. Wacker Drive Suite 1700",
      city: "Chicago",
      region: "Il 60606",
    },
    country: "US",
    role: "CEO",
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "EVRAZ GROUP S.A.",
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 96.85,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
  {
    id: faker.datatype.uuid(),
    name: "COMPUTERSHARE DR NOMINEES LIMITED",
    keyPartyType: "shareholder",
    entityType: ProfileTypes.company,
    sharesPercentage: 3.14,
    createdAt: "2023-08-07T07:15:05.192931Z",
    createdById: 101551117,
    createdBy: "Supriya Shastri",
    modifiedAt: "2023-10-04T14:34:52.404785Z",
    modifiedById: 101563643,
    modifiedBy: "Venkat Surya Kiran",
  },
];

export const GenerateUboDetailsResponse = (noOfRecords = 1): UboDetails[] => {
  return Array.from({ length: noOfRecords }, () => ({
    id: faker.datatype.uuid(),
    name: faker.name.firstName(),
    firstName: faker.name.firstName(),
    middleName: faker.name.firstName(),
    lastName: faker.name.firstName(),
    keyPartyType: faker.helpers.arrayElement([
      "director",
      "shareholder",
      "ubo",
    ]),
    entityType: faker.helpers.arrayElement([
      ProfileTypes.individual,
      ProfileTypes.company,
      ProfileTypes.trust,
      ProfileTypes.otherEntity,
      ProfileTypes.partnership,
      ProfileTypes.soleTrader,
      ProfileTypes.plc,
    ]),
    gender: faker.helpers.arrayElement(["female", "male"]),
    dateOfBirth: faker.date.past().toISOString(),
    country: faker.address.countryCode("alpha-2").toLowerCase(),
    organisationNumber: faker.finance.account(6).toString(),
    safeNumber: faker.random.alphaNumeric(5),
    activityCode: faker.finance.account(5).toString(),
    sharesPercentage: faker.datatype.number({ min: 0, max: 99 }),
    role: "CEO",
    address: {
      description:
        faker.address.buildingNumber() + " " + faker.address.street(),

      buildingDetails:
        faker.address.buildingNumber() + " " + faker.address.street(),

      street: faker.address.street(),
      city: faker.address.city(),
      region: faker.address.county(),
      postalCode: faker.address.zipCode(),
    },
    createdBy: faker.name.firstName(),
    createdById: Number(faker.random.numeric(5)),
    createdAt: faker.date.past().toISOString(),
    modifiedAt: faker.date.recent().toISOString(),
    modifiedById: Number(faker.random.numeric(5)),
    modifiedBy: faker.name.firstName(),
  }));
};

export const generateNotes = (): ViewNotes => {
  return {
    linkedTo: faker.random.word(),
    id: faker.datatype.uuid(),
    body: faker.random.word(),
    isArchived: false,
    createdAt: faker.date.past().toISOString(),
    createdById: faker.datatype.number(6),
    createdBy: faker.name.fullName(),
    modifiedAt: faker.date.past().toISOString(),
    modifiedById: 101565589,
    modifiedBy: faker.name.fullName(),
    count: faker.datatype.number(2),
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const NotesBuilder = (count: number) => {
  return {
    count,
    apiFailed: faker.datatype.boolean(),
    items: Array.from({ length: count }, () => generateNotes()),
  };
};
